import { useMutation, useQuery } from '@tanstack/react-query';
import { EllipsisVertical } from 'lucide-react';

import { Button } from '@nesso/ui/primitives/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@nesso/ui/primitives/dropdown-menu';

import { type Session, useDeleteSessionOptions } from '@/entities/session';
import { useDeleteNoteOptions, useIsGenerating } from '@/entities/session-note';
import { transcriptMetadataOptions } from '@/entities/transcript';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';
import { useSheetStore } from '@/shared/stores/sheet-store';

import { useSessionId } from '../../../model/use-session-id';

type Props = {
    session: Session;
    compact?: boolean;
};

export function SessionDetailsHeaderActionsMenu({ session, compact }: Props) {
    const sessionId = useSessionId();
    const closeCurrentSheet = useSheetStore((state) => state.closeCurrentSheet);
    const deleteSessionMutation = useMutation(useDeleteSessionOptions());
    const deleteNoteMutation = useMutation(useDeleteNoteOptions(sessionId));
    const { data: metadata, isPending: isPendingMetadata } = useQuery(
        transcriptMetadataOptions(sessionId),
    );
    const isGenerating = useIsGenerating(sessionId);
    const openConfirmDialog = useConfirmDialogStore((state) => state.open);
    const noteId = session.currentNote?.id;
    const isNoteDeleted =
        session.generatedNoteDeletedAt !== null && session.transcriptDeletedAt !== null;
    const isDeleteNoteDisabled =
        isGenerating || isNoteDeleted || !session.currentNote?.content || isPendingMetadata;

    const confirmDeleteSession = () => {
        openConfirmDialog({
            title: 'Delete session?',
            description:
                'The note and transcript associated with this session will also be deleted.',
            cancelText: 'Cancel',
            confirmText: 'Delete Session',
            variant: 'destructive',
            onConfirm: () => {
                deleteSessionMutation.mutate(sessionId, {
                    onSuccess: () => closeCurrentSheet(),
                });
            },
        });
    };

    const confirmDeleteNote = () => {
        openConfirmDialog({
            title: 'Delete note?',
            description: metadata?.expiresAt
                ? null
                : 'This note cannot be regenerated because the transcript no longer exists.',
            cancelText: 'Cancel',
            confirmText: 'Delete Note',
            variant: 'destructive',
            width: metadata?.expiresAt ? '2xs' : undefined,
            onConfirm: () => {
                if (noteId) deleteNoteMutation.mutate(noteId);
            },
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className={`${compact ? 'size-7' : 'size-9'} data-[state=open]:bg-accent/50 data-[state=open]:text-accent-foreground`}
                    aria-label="Session options"
                >
                    <EllipsisVertical className={compact ? 'size-4' : 'size-5'} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-40" sideOffset={1}>
                <DropdownMenuItem onClick={confirmDeleteNote} disabled={isDeleteNoteDisabled}>
                    Delete Note
                </DropdownMenuItem>
                <DropdownMenuItem onClick={confirmDeleteSession}>Delete Session</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
